import { z } from 'zod'

export const ownedCardSchema = z.object({
  workId: z.string(),
  cardId: z.string(),
  id: z.string().optional(),
  sourceCardId: z.string().nullable().optional(),
  image: z.string().optional(),
})

const reasoningPolicySchema = z.looseObject({
  deepTestEnabled: z.boolean().optional(),
})

/** 계정별 모델 설정은 bootstrap에서만 받아들인다. 알 수 없는 신필드는 보존한다. */
export const chatModelSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  family: z.string(),
  provider: z.string(),
  preview: z.boolean(),
  enabled: z.boolean(),
  reasoning: z.array(z.string()).optional(),
  reasoningPolicy: reasoningPolicySchema.optional(),
  configured: z.boolean().optional(),
  tier: z.string().optional(),
  recommendedModes: z.array(z.string()).optional(),
  maxTokens: z.record(z.string(), z.number()).optional(),
  cost: z.record(z.string(), z.number()).optional(),
  costUsd: z.string().nullable().optional(),
})

export const operationFailureSchema = z.looseObject({
  code: z.string(),
  category: z.string(),
  message: z.string(),
  retryable: z.boolean(),
  reason: z.string().nullable().optional(),
  options: z.array(z.unknown()).optional(),
})

export const segmentSchema = z
  .looseObject({
    type: z.enum(['speech', 'narration', 'image', 'user']),
    text: z.string().optional(),
    id: z.string().optional(),
    speaker: z.string().optional(),
    speakerId: z.string().nullable().optional(),
    speakerType: z.string().optional(),
    targetIds: z.array(z.string()).optional(),
    channel: z.string().optional(),
    provenance: z
      .union([z.record(z.string(), z.unknown()), z.string()])
      .optional(),
  })
  .superRefine((segment, context) => {
    if (segment.type !== 'image' && segment.text === undefined)
      context.addIssue({
        code: 'custom',
        path: ['text'],
        message: '본문이 없습니다.',
      })
  })
  .transform((segment) => ({ ...segment, text: segment.text ?? '' }))

export const operationSchema = z.looseObject({
  id: z.string(),
  requestId: z.string(),
  status: z.enum([
    'queued',
    'running',
    'complete',
    'failed',
    'cancelled',
    'invalidated',
  ]),
  error: z.string().optional(),
  failure: operationFailureSchema.nullable().optional(),
  result: z.record(z.string(), z.unknown()).nullable().optional(),
  room: z.unknown().optional(),
  wallet: z.unknown().optional(),
})

const messageSchema = z.looseObject({
  id: z.string(),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string(),
  segments: z.array(segmentSchema).optional(),
  turnId: z.string().nullable().optional(),
  requestId: z.string().optional(),
})
export const messagePageSchema = z
  .object({
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    nextBeforeId: z.string().min(1).nullable(),
  })
  .refine(
    (page) =>
      page.start <= page.end &&
      page.end <= page.total &&
      (page.nextBeforeId === null || page.start > 0),
    '메시지 범위가 올바르지 않습니다.'
  )

export const messageHistorySchema = z
  .object({
    messages: z.array(messageSchema),
    total: z.number().int().nonnegative(),
    nextBefore: z.number().int().nonnegative().nullable(),
    page: messagePageSchema,
  })
  .refine(
    (value) =>
      value.messages.length === value.page.end - value.page.start &&
      value.total === value.page.total &&
      new Set(value.messages.map((message) => message.id)).size ===
        value.messages.length &&
      (value.page.nextBeforeId === null ||
        value.page.nextBeforeId === value.messages[0]?.id),
    '메시지 개수 또는 커서가 범위와 다릅니다.'
  )
const statusSchema = z.looseObject({
  date: z.string().optional(),
  minutes: z.number().nullable().optional(),
  time: z.number().nullable().optional(),
  noTime: z.boolean().optional(),
  place: z.string().optional(),
})
export const roomSchema = z
  .looseObject({
    id: z.string(),
    workId: z.string(),
    locked: z.boolean().optional(),
    messages: z.array(messageSchema),
    oocMessages: z.array(messageSchema).optional(),
    messagePage: messagePageSchema.optional(),
    status: statusSchema.optional(),
    model: z.string().optional(),
    preset: z.string().optional(),
  })
  .refine(
    (room) => room.locked === true || room.status !== undefined,
    '현재 장면 상태가 없습니다.'
  )
  .refine(
    (room) =>
      !room.messagePage ||
      room.messages.length === room.messagePage.end - room.messagePage.start,
    '메시지 개수가 범위와 다릅니다.'
  )

export const adultStatusSchema = z.looseObject({
  eligible: z.boolean(),
  reason: z.string().nullable().optional(),
  minAge: z.number().int().positive().optional(),
  verifiedAt: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
})

export const bootstrapEnvelopeSchema = z.looseObject({
  user: z
    .looseObject({ id: z.string(), name: z.string(), guest: z.boolean() })
    .nullable(),
  works: z.array(z.unknown()).optional(),
  rooms: z.array(
    z.looseObject({
      id: z.string(),
      workId: z.string(),
      locked: z.boolean().optional(),
    })
  ),
  wallet: z.looseObject({ balance: z.number() }),
  config: z.looseObject({
    creator: z
      .looseObject({
        enabled: z.boolean().optional(),
        reviewRequired: z.boolean().optional(),
        creationReceipts: z.boolean().optional(),
      })
      .optional(),
    adult: adultStatusSchema.optional(),
    models: z.array(chatModelSchema).optional(),
    reasoningPolicy: reasoningPolicySchema.optional(),
    presets: z.array(z.looseObject({ id: z.string() })).optional(),
    modes: z.array(z.looseObject({ id: z.string() })).optional(),
  }),
})
